import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory, Link } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PublicLayout from '../../layouts/PublicLayout';
import PasswordStrength from '../../components/auth/PasswordStrength';
import PasswordField from '../../components/common/PasswordField';
import {
  validateInviteToken,
  registerWithInvite,
  clearCurrentInvite,
  clearActionError
} from '../../redux/slices/inviteSlice';
import { getCurrentUser } from '../../redux/slices/authSlice';
import { setToken } from '../../utils/storageUtils';

/**
 * Register with Invite Page
 * 
 * For NEW users to register with a company invite token
 * Phase 4 M2: User Invite System
 */
const RegisterWithInvitePage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { token } = useParams();

  const {
    currentInvite,
    currentInviteLoading,
    currentInviteError,
    actionLoading,
    actionError
  } = useSelector((state) => state.invite);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Load invite on mount
  useEffect(() => {
    if (token) {
      dispatch(validateInviteToken(token));
    }

    // Cleanup on unmount
    return () => {
      dispatch(clearCurrentInvite());
      dispatch(clearActionError());
    };
  }, [dispatch, token]);

  // Redirect if existing user
  useEffect(() => {
    if (currentInvite && currentInvite.isExistingUser) {
      // This invite is for existing users, redirect to accept page
      history.push(`/invite/accept/${token}`);
    }
  }, [currentInvite, token, history]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Ad gereklidir';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Soyad gereklidir';
    }

    if (!formData.password) {
      newErrors.password = 'Şifre gereklidir';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Şifre en az 8 karakter olmalıdır';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Şifre tekrarı gereklidir';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Şifreler eşleşmiyor';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      const result = await dispatch(registerWithInvite({
        token,
        userData: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          password: formData.password
        }
      })).unwrap();

      // Store token in localStorage
      setToken(result.token);

      // Fetch current user to update Redux state
      await dispatch(getCurrentUser()).unwrap();

      // Show success
      setRegistrationSuccess(true);

      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        history.push('/dashboard');
      }, 3000);
    } catch (err) {
      console.error('Registration error:', err);
    }
  };

  // Loading state
  if (currentInviteLoading) {
    return (
      <PublicLayout>
        <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3 }}>
            Davet bilgileri yükleniyor...
          </Typography>
        </Container>
      </PublicLayout>
    );
  }

  // Error state
  if (currentInviteError) {
    return (
      <PublicLayout>
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Alert severity="error">
            <Typography variant="h6" gutterBottom>
              Davet Bulunamadı
            </Typography>
            <Typography variant="body2">
              {currentInviteError}
            </Typography>
          </Alert>
          <Button
            variant="contained"
            component={Link}
            to="/login"
            sx={{ mt: 3 }}
          >
            Giriş Yap
          </Button>
        </Container>
      </PublicLayout>
    );
  }

  // Success state
  if (registrationSuccess) {
    return (
      <PublicLayout>
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" color="success.main" gutterBottom>
              Kayıt Başarılı!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Hoş geldiniz! Şirkete başarıyla katıldınız.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Dashboard'a yönlendiriliyorsunuz...
            </Typography>
            <CircularProgress size={30} sx={{ mt: 3 }} />
          </Paper>
        </Container>
      </PublicLayout>
    );
  }

  // Main form
  if (!currentInvite) {
    return (
      <PublicLayout>
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Alert severity="info">
            Davet bilgileri yükleniyor...
          </Alert>
        </Container>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <Container maxWidth="sm" sx={{ py: 6 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <BusinessIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Şirket Daveti
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>{currentInvite.companyId?.companyName}</strong> şirketi sizi davet ediyor
          </Typography>
        </Box>

        {/* Error Alert */}
        {actionError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(clearActionError())}>
            {actionError}
          </Alert>
        )}

        {/* Registration Form */}
        <Paper sx={{ p: 4 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Kayıt Ol
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Daveti kabul etmek için lütfen kayıt olun
          </Typography>

          <form onSubmit={handleSubmit}>
            {/* Email (pre-filled, readonly) */}
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={currentInvite.email}
              disabled
              sx={{ mb: 2 }}
              helperText="Bu email adresi ile kayıt olacaksınız"
            />

            {/* First Name */}
            <TextField
              fullWidth
              label="Ad"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              error={!!errors.firstName}
              helperText={errors.firstName}
              disabled={actionLoading}
              required
              sx={{ mb: 2 }}
            />

            {/* Last Name */}
            <TextField
              fullWidth
              label="Soyad"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              error={!!errors.lastName}
              helperText={errors.lastName}
              disabled={actionLoading}
              required
              sx={{ mb: 2 }}
            />

            {/* Password */}
            <PasswordField
              label="Şifre"
              name="password"
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              disabled={actionLoading}
              required
              margin="none"
              autoComplete="new-password"
              sx={{ mb: 1 }}
            />

            {/* Password Strength */}
            {formData.password && (
              <Box sx={{ mb: 2 }}>
                <PasswordStrength password={formData.password} />
              </Box>
            )}

            {/* Confirm Password */}
            <PasswordField
              label="Şifre Tekrar"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              disabled={actionLoading}
              required
              margin="none"
              autoComplete="new-password"
              sx={{ mb: 3 }}
            />

            <Divider sx={{ my: 3 }} />

            {/* Info */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Kayıt olarak <strong>{currentInvite.companyId?.companyName}</strong> şirketine katılacaksınız.
              </Typography>
            </Alert>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={actionLoading}
              startIcon={actionLoading && <CircularProgress size={20} color="inherit" />}
            >
              {actionLoading ? 'Kayıt Yapılıyor...' : 'Kayıt Ol ve Daveti Kabul Et'}
            </Button>
          </form>

          {/* Already have account */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Zaten hesabınız var mı?{' '}
              <Link to="/login" style={{ color: '#1976d2', textDecoration: 'none' }}>
                Giriş Yap
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </PublicLayout>
  );
};

export default RegisterWithInvitePage;

